/**
 * Bulk charge fundraising guarantee helpers
 *
 * Pure functions only: no network, no storage, no clock reads. Time and ledger
 * entries are passed in so every decision on this screen can be tested
 * directly.
 */

import { I18n } from 'react-redux-i18n';
import CurrencyHelper from '../../../helpers/currencyHelper';
import { isFundraisingGuaranteeAvailable } from '../../vipProgram/helpers/fundraisingGuarantee';
import {
  CHARGEABLE_STATUSES,
  CHARGE_ERROR_CLASSES,
  CHARGE_ERROR_I18N_KEYS,
  CSV_FILENAME_PREFIX,
  CSV_UNKNOWN_CHARITY_SUFFIX,
  LIVE_CAMPAIGN_STATUS,
  amountUnknownReason,
  eligibilityReason,
  eligibilityVerdict,
  i18nKeys,
  outcomeClass,
  rowOutcome
} from './constants';
import { describeRecentCharge, findBlockingEntry } from './recentCharges';
import {
  AmountEstimate,
  CharityGroup,
  CurrencyTotal,
  EligibilityReason,
  EventAmbassador,
  OutcomeClass,
  ParseFeedback,
  RecentChargeEntry,
  RowEligibility,
  SummaryRow
} from './types';

/* ────────────────────────── id parsing ────────────────────────── */

export interface ParsedIds extends ParseFeedback {
  ids: number[];
}

const ID_SEPARATORS = /[\s,;]+/;

/**
 * Parse a pasted blob into ids.
 *
 * Deliberately permissive about separators (new lines, commas, spaces, semicolons,
 * tabs) and about surrounding quotes, because the input is usually pasted from a
 * spreadsheet or an email. Deliberately strict about what counts as an id: only
 * positive safe integers. Anything else is reported rather than coerced, because
 * silently turning "12.5" into 12 would charge the wrong person.
 */
export const parseAmbassadorIds = (raw: string): ParsedIds => {
  const tokens = (raw || '').split(ID_SEPARATORS).filter(Boolean);

  const ids: number[] = [];
  const seen = new Set<number>();
  const duplicates: number[] = [];
  const duplicatesSeen = new Set<number>();
  const invalid: string[] = [];
  const invalidSeen = new Set<string>();

  tokens.forEach((token) => {
    const cleaned = token.replace(/^["']+|["']+$/g, '').trim();
    if (!cleaned) {
      return;
    }

    if (!/^\d+$/.test(cleaned)) {
      if (!invalidSeen.has(cleaned)) {
        invalidSeen.add(cleaned);
        invalid.push(cleaned);
      }
      return;
    }

    const parsed = Number(cleaned);
    if (!Number.isSafeInteger(parsed) || parsed <= 0) {
      if (!invalidSeen.has(cleaned)) {
        invalidSeen.add(cleaned);
        invalid.push(cleaned);
      }
      return;
    }

    if (seen.has(parsed)) {
      if (!duplicatesSeen.has(parsed)) {
        duplicatesSeen.add(parsed);
        duplicates.push(parsed);
      }
      return;
    }

    seen.add(parsed);
    ids.push(parsed);
  });

  return { ids, invalid, duplicates, overflow: 0 };
};

/**
 * Render ids back into the textarea. Once a summary exists the id list is the
 * source of truth and the textarea is regenerated from it, so that what the
 * operator sees is always exactly what will be charged.
 */
export const serializeAmbassadorIds = (ids: number[]): string => ids.join('\n');

/* ────────────────────────── amount ────────────────────────── */

/**
 * The shortfall to be charged.
 *
 * The API resolves the minimum target through EventAmbassador, then
 * CampaignAmbassadorProgram, then EventAmbassadorProgram, but the GET only
 * exposes the ambassador level value. When that is null the real figure is set
 * at program level and we cannot see it, so the amount is unknown. Unknown is
 * never zero and must not be rendered as a zero amount.
 */
export const computeAmountToCharge = (ambassador: EventAmbassador | null): AmountEstimate => {
  if (!ambassador) {
    return { amount: null, unknownReason: amountUnknownReason.noEaMinimum };
  }
  if (!ambassador.fundraiser) {
    return { amount: null, unknownReason: amountUnknownReason.noFundraiser };
  }
  if (
    ambassador.fundraiserMinimumTargetAmount === null ||
    ambassador.fundraiserMinimumTargetAmount === undefined
  ) {
    return { amount: null, unknownReason: amountUnknownReason.noEaMinimum };
  }

  const raised = ambassador.fundraiser.raisedAmount ?? 0;
  return {
    amount: Math.max(0, ambassador.fundraiserMinimumTargetAmount - raised),
    unknownReason: null
  };
};

/* ────────────────────────── eligibility ────────────────────────── */

/**
 * Mirror the API's pre-flight checks so ineligible rows are visible before
 * anyone ticks a confirmation box, and blocked before anything is sent.
 *
 * Three server checks are deliberately NOT mirrored: `Id.eventStatus`,
 * `Id.donationPlanEnabled` and `Id.campaignRequirements`. The GET does not
 * expose what they need. They are safe pre-flight rejections, so letting the
 * server own them costs nothing beyond a wasted round trip.
 *
 * Note `isFundraisingDeadlinePassed` and `hasMinimumTargetBeenMet` are derived
 * server-side from the ambassador level fields, so they read false when those
 * fields are null rather than signalling "unknown". That is why the
 * unavailable-field checks come first: without them, a null minimum would look
 * like a target that has not been met and a null deadline like one that has not
 * passed.
 */
export const deriveRowEligibility = (
  ambassador: EventAmbassador | null,
  recentEntries: RecentChargeEntry[] = [],
  now: number = 0
): RowEligibility => {
  const reasons: EligibilityReason[] = [];

  if (!ambassador) {
    return { verdict: eligibilityVerdict.blocked, reasons: [eligibilityReason.fetchFailed] };
  }

  // Double charge guards first: these are the ones that cost real money.
  if (
    ambassador.chargedViaDonationPlan === true ||
    (ambassador.donationPlanChargedAmount ?? 0) > 0
  ) {
    reasons.push(eligibilityReason.alreadyCharged);
  }

  if (findBlockingEntry(recentEntries, ambassador.id, now)) {
    reasons.push(eligibilityReason.chargedRecently);
  }

  // The guarantee cannot be offered in the UK. `hasDonationPlan` only says a
  // card is on file, not that a guarantee was agreed to, so the country is
  // checked on its own.
  if (!isFundraisingGuaranteeAvailable(ambassador.organization?.country)) {
    reasons.push(eligibilityReason.ukCharity);
  }

  // Fields we need but may not have been given.
  if (!ambassador.fundraiser) {
    reasons.push(eligibilityReason.noFundraiser);
  }
  if (
    ambassador.fundraiserMinimumTargetAmount === null ||
    ambassador.fundraiserMinimumTargetAmount === undefined
  ) {
    reasons.push(eligibilityReason.amountUnavailable);
  }
  if (!ambassador.fundraiseBy) {
    reasons.push(eligibilityReason.fundraiseByUnavailable);
  }

  // Mirrored pre-flight checks.
  if (!CHARGEABLE_STATUSES.includes(ambassador.status)) {
    reasons.push(eligibilityReason.statusInvalid);
  }
  if (
    ambassador.campaign?.status &&
    ambassador.campaign.status !== LIVE_CAMPAIGN_STATUS
  ) {
    reasons.push(eligibilityReason.campaignNotLive);
  }
  if (ambassador.hasDonationPlan === false) {
    reasons.push(eligibilityReason.noCardOnFile);
  }
  if (ambassador.hasMinimumTargetBeenMet === true) {
    reasons.push(eligibilityReason.minimumAlreadyMet);
  }
  // Only meaningful when a deadline actually exists; otherwise
  // `fundraiseByUnavailable` above already blocks the row.
  if (ambassador.fundraiseBy && ambassador.isFundraisingDeadlinePassed === false) {
    reasons.push(eligibilityReason.deadlineNotPassed);
  }

  return {
    verdict: reasons.length ? eligibilityVerdict.blocked : eligibilityVerdict.eligible,
    reasons
  };
};

/** Resolved copy for a blocked reason, including the recent-charge countdown. */
export const eligibilityReasonText = (
  reason: EligibilityReason,
  ambassadorId: number,
  recentEntries: RecentChargeEntry[] = [],
  now: number = 0
): string => {
  if (reason === eligibilityReason.chargedRecently) {
    const entry = findBlockingEntry(recentEntries, ambassadorId, now);
    if (entry) {
      const { elapsedMinutes, remainingMinutes } = describeRecentCharge(entry, now);
      return I18n.t(i18nKeys.ELIGIBILITY.REASONS.chargedRecently, {
        elapsed: formatMinutes(elapsedMinutes),
        remaining: formatMinutes(remainingMinutes)
      });
    }
  }
  return I18n.t(i18nKeys.ELIGIBILITY.REASONS[reason]);
};

const formatMinutes = (minutes: number): string =>
  minutes === 1 ? '1 minute' : `${minutes} minutes`;

/* ────────────────────────── outcome classification ────────────────────────── */

/**
 * Classify a charge response.
 *
 * Matching is exact and case-sensitive on purpose. Pre-flight codes use `Id.*`
 * with a capital I, while the legacy mid-transaction code is `id.notFound` in
 * lowercase. Case-folding them together would merge "definitely no money moved"
 * with "money may have moved".
 *
 * Anything unrecognised is indeterminate. An unexpected response is never
 * evidence that nothing was charged.
 */
export const classifyChargeOutcome = (
  httpStatus: number | null,
  code: string | null
): OutcomeClass => {
  if (httpStatus !== null && httpStatus >= 200 && httpStatus < 300) {
    return outcomeClass.submitted;
  }
  if (httpStatus === 409) {
    return outcomeClass.indeterminate;
  }
  if (code && Object.prototype.hasOwnProperty.call(CHARGE_ERROR_CLASSES, code)) {
    return CHARGE_ERROR_CLASSES[code];
  }
  return outcomeClass.indeterminate;
};

/**
 * Resolved, operator-facing copy for a charge failure.
 *
 * Returns empty for a success. A 2xx carries no error code, and without this
 * guard it would fall through to the "could not be completed" message, putting
 * a failure message against a charge that went through.
 */
export const chargeErrorMessage = (
  httpStatus: number | null,
  code: string | null
): string => {
  if (httpStatus !== null && httpStatus >= 200 && httpStatus < 300) {
    return '';
  }
  if (httpStatus === 409) {
    return I18n.t(i18nKeys.CHARGE_ERRORS.CONFLICT);
  }
  if (code && Object.prototype.hasOwnProperty.call(CHARGE_ERROR_I18N_KEYS, code)) {
    return I18n.t(CHARGE_ERROR_I18N_KEYS[code]);
  }
  if (!code) {
    // A request that never completed and one that came back 500 with no usable
    // body are both unknown outcomes, but they are not the same failure and
    // send the operator looking in different places. This message also ends up
    // in the CSV the charity receives.
    return httpStatus === null
      ? I18n.t(i18nKeys.CHARGE_ERRORS.NETWORK)
      : I18n.t(i18nKeys.CHARGE_ERRORS.HTTP_ERROR, { status: httpStatus });
  }
  return I18n.t(i18nKeys.CHARGE_ERRORS.UNKNOWN, { code });
};

/* ────────────────────────── idempotency ────────────────────────── */

/**
 * A stable key per ambassador per batch, so a double-fired button or a browser
 * level retry replays the cached response instead of charging again.
 *
 * The server ignores the literal strings "undefined" and "null", so guard the
 * batch id rather than trusting the caller.
 */
export const buildIdempotencyKey = (batchId: string | null, ambassadorId: number): string => {
  const safeBatchId = batchId && batchId !== 'undefined' && batchId !== 'null' ? batchId : 'nobatch';
  return `ea-charge-${ambassadorId}-${safeBatchId}`;
};

/* ────────────────────────── CSV ────────────────────────── */

/*
 * Column headers are stable English keys rather than translated strings. This
 * file is read by people reconciling against Stripe and by spreadsheets, so the
 * column names have to stay put even if the UI copy changes.
 */
const CSV_COLUMNS = [
  'eaId',
  'firstName',
  'lastName',
  'email',
  'organizationId',
  'organizationName',
  'currency',
  'eventName',
  'campaignName',
  'fundraiseBy',
  'amountToChargeEstimate',
  'eligibility',
  'blockedReasons',
  'outcome',
  'errorCode',
  'errorMessage',
  'httpStatus',
  'attemptedAtUtc',
  'verifiedChargedViaDonationPlan',
  'verifiedChargedAmount'
];

/**
 * Names and emails are supplied by ambassadors and this file gets opened in a
 * spreadsheet, so neutralise anything that could be read as a formula.
 *
 * Leading whitespace is skipped before the check. Excel itself does not
 * evaluate a cell starting with a space, but consumers that trim on import do,
 * and this guard already treated a leading tab or carriage return as unsafe.
 * There is no principled reason to catch those two and miss a space.
 *
 * The original value is prefixed rather than trimmed, so the guard never
 * silently alters the data it is protecting.
 */
const escapeFormula = (value: string): string =>
  /^\s*[=+\-@]/.test(value) ? `'${value}` : value;

const csvCell = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '""';
  }
  const asString = typeof value === 'string' ? value : String(value);
  return `"${escapeFormula(asString).replace(/"/g, '""')}"`;
};

const toIso = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? value : new Date(parsed).toISOString();
};

/**
 * Build the results file.
 *
 * Amounts stay raw with a separate currency column and dates stay ISO 8601, so
 * the output is machine readable. Formatted currency would be unparseable
 * downstream. Failed rows carry both the raw wire code and the readable message
 * the operator saw, so the charity does not have to decode error codes and the
 * team can still grep by code.
 */
export const buildResultsCsv = (rows: SummaryRow[]): string => {
  const lines = [CSV_COLUMNS.map(csvCell).join(',')];

  rows.forEach((row) => {
    const ambassador = row.ambassador;
    // Only failures carry a message. Keying this off the outcome rather than
    // off "is there a status code" keeps a successful 201 out of it.
    const failed = row.outcome === rowOutcome.rejected || row.outcome === rowOutcome.indeterminate;
    const errorMessage = failed ? chargeErrorMessage(row.httpStatus, row.errorCode) : '';

    lines.push(
      [
        row.id,
        ambassador?.firstName ?? '',
        ambassador?.lastName ?? '',
        ambassador?.email ?? '',
        ambassador?.organization?.id ?? '',
        ambassador?.organization?.name ?? '',
        ambassadorCurrency(ambassador) ?? '',
        ambassador?.event?.name ?? '',
        ambassador?.campaign?.name ?? '',
        toIso(ambassador?.fundraiseBy),
        row.amount.amount === null ? '' : row.amount.amount,
        row.eligibility.verdict,
        row.eligibility.reasons.join('; '),
        row.outcome,
        row.errorCode ?? '',
        errorMessage,
        row.httpStatus ?? '',
        row.attemptedAtUtc ?? '',
        row.verification?.chargedViaDonationPlan === null ||
        row.verification?.chargedViaDonationPlan === undefined
          ? ''
          : String(row.verification.chargedViaDonationPlan),
        row.verification?.donationPlanChargedAmount ?? ''
      ]
        .map(csvCell)
        .join(',')
    );
  });

  // BOM so Excel reads it as UTF-8, CRLF per RFC 4180.
  return `﻿${lines.join('\r\n')}\r\n`;
};

const pad = (value: number): string => String(value).padStart(2, '0');

/** Lower case, alphanumeric and dashes only, so it is safe in a filename. */
export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

/**
 * The filename is the last safeguard against sending the wrong file to the
 * wrong charity. Several exports downloaded a minute apart would otherwise
 * differ only by their timestamp, sitting in a downloads folder about to be
 * attached to several different emails, so the charity name goes in the name
 * itself.
 *
 * There is no combined export, so a charity name is always expected. A group
 * whose charity failed to load falls back to `unknown-charity`.
 */
export const buildCsvFilename = (date: Date, charityName?: string | null): string => {
  const stamp = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(
    date.getHours()
  )}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  const slug = charityName ? slugify(charityName) : '';
  return `${CSV_FILENAME_PREFIX}-${slug || CSV_UNKNOWN_CHARITY_SUFFIX}-${stamp}.csv`;
};

/* ────────────────────────── row helpers ────────────────────────── */

export const isChargeable = (row: SummaryRow): boolean =>
  row.eligibility.verdict === eligibilityVerdict.eligible && row.outcome === rowOutcome.pending;

/**
 * The currency a guarantee is charged in: the currency the minimum was locked
 * in, falling back to the charity currency for rows created before it was
 * recorded.
 */
export const ambassadorCurrency = (
  ambassador: EventAmbassador | null | undefined
): string | null =>
  ambassador?.fundraiserMinimumTargetCurrency ??
  ambassador?.organization?.currency ??
  null;

/**
 * Total of the rows that will actually be charged.
 *
 * Only safe within a single currency. Use `sumChargeableByCurrency` for a
 * whole batch, which can span charities and therefore currencies.
 */
export const sumChargeableAmount = (rows: SummaryRow[]): number =>
  rows.filter(isChargeable).reduce((total, row) => total + (row.amount.amount ?? 0), 0);

/**
 * Chargeable totals split by currency.
 *
 * Adding a batch together regardless of currency produces one wrong number
 * wearing one currency's symbol, on the panel where the operator confirms real
 * charges. Every total on this screen goes through here.
 */
export const sumChargeableByCurrency = (rows: SummaryRow[]): CurrencyTotal[] => {
  const totals: CurrencyTotal[] = [];
  const indexByCurrency = new Map<string, number>();

  rows.filter(isChargeable).forEach((row) => {
    // A chargeable row has always loaded, so it always carries an organisation.
    const currency = ambassadorCurrency(row.ambassador);
    const key = currency ?? '';
    const existing = indexByCurrency.get(key);

    if (existing === undefined) {
      indexByCurrency.set(key, totals.length);
      totals.push({ currency, amount: row.amount.amount ?? 0, count: 1 });
      return;
    }

    totals[existing].amount += row.amount.amount ?? 0;
    totals[existing].count += 1;
  });

  return totals;
};

/**
 * Group rows by charity, then by event within each charity, preserving the
 * order each first appears in the pasted list.
 *
 * Charity is the outer level because it is the export boundary. Each charity
 * gets its own CSV, and a file sent to one charity must never contain another
 * charity's ambassadors, so the grouping the operator sees and the grouping the
 * files are cut along have to be the same.
 */
export const groupSummaryRows = (rows: SummaryRow[]): CharityGroup[] => {
  const groups: CharityGroup[] = [];
  const indexByCharity = new Map<string, number>();

  rows.forEach((row) => {
    const organizationId = row.ambassador?.organization?.id ?? null;
    const eventName = row.ambassador?.event?.name ?? null;
    // Rows that failed to load have no charity and collect together.
    const charityKey = row.ambassador ? `org-${organizationId ?? 'none'}` : 'unloaded';
    const eventKey = `event-${eventName ?? 'none'}`;

    let charityIndex = indexByCharity.get(charityKey);
    if (charityIndex === undefined) {
      charityIndex = groups.length;
      indexByCharity.set(charityKey, charityIndex);
      groups.push({
        key: charityKey,
        organizationId,
        charityName: row.ambassador?.organization?.name ?? null,
        currency: row.ambassador?.organization?.currency ?? null,
        events: [],
        rows: []
      });
    }

    const charity = groups[charityIndex];
    const event = charity.events.find((candidate) => candidate.key === eventKey);
    if (event) {
      event.rows.push(row);
    } else {
      charity.events.push({ key: eventKey, eventName, rows: [row] });
    }
  });

  // Rebuild each charity's flat row list in event order, so the table and its
  // CSV agree on ordering.
  groups.forEach((group) => {
    group.rows = group.events.flatMap((event) => event.rows);
  });

  return groups;
};


export const fullName = (row: SummaryRow): string =>
  [row.ambassador?.firstName, row.ambassador?.lastName].filter(Boolean).join(' ');

/**
 * An aggregated amount, with its currency code.
 *
 * Most currencies here render as a bare "$" under narrowSymbol, so two totals
 * in different currencies are indistinguishable without the code. Every
 * aggregate on this screen goes through this, since those are exactly the
 * figures that sit next to each other and get compared.
 *
 * The code is omitted rather than guessed when the currency is unknown:
 * labelling an unknown amount "AUD" would be worse than not labelling it.
 */
export const formatAmountWithCurrency = (
  amount: number,
  currency: string | null | undefined
): string => {
  const formatted = CurrencyHelper.formatCurrency(amount, currency, {
    alwaysShowDecimals: true
  });
  return currency ? `${formatted} ${currency.toUpperCase()}` : formatted;
};

export const countByOutcome = (rows: SummaryRow[]) => ({
  submitted: rows.filter((row) => row.outcome === rowOutcome.submitted).length,
  rejected: rows.filter((row) => row.outcome === rowOutcome.rejected).length,
  indeterminate: rows.filter((row) => row.outcome === rowOutcome.indeterminate).length,
  blocked: rows.filter((row) => row.outcome === rowOutcome.blocked).length
});

/** Split a list into fixed size chunks, for the bounded concurrency reads. */
export const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};
